// IngredientFormPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, TextField, Grid, Paper, Stack } from "@mui/material";
import { useLocation } from "react-router-dom";
import Layout from "../utils/Layout";
import { HeadingTitle } from "../utils/HeadingTitle";
import { getFormFields } from "../../helpers/formFields";
import { Subsection } from "../utils/Subsection";
import AlertBar from "../utils/AlertBar";
import { API_URL } from "../../config/environment";
import { useTitle } from "../utils/useTitle";

export const IngredientForm = () => {
  useTitle("New Ingredient");
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { ingredientData } = location.state || {};
  const token = sessionStorage.getItem("authToken");

  const [ingredient, setIngredient] = useState({});
  const [originalIngredient, setOriginalIngredient] = useState({});

  const [formFields, setFormFields] = useState([]);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  function handleIsSuccessState() {
    setIsSuccess(false);
  }

  function handleIsErrorState() {
    setIsError(false);
  }

  async function createIngredient(ingredient) {
    const url = `${API_URL}/ingredients`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ingredient),
      });

      if (response.status === 403) {
        setIsError(true);
        return setErrorMessage("You are not permitted to create ingredients");
      }

      const data = await response.json();
      if (data.status === "success") {
        setIsSuccess(true);
        setSuccessMessage("Ingredient created successfully");
        setIngredient({});
        setFormFields([]);
        navigate("/dashboard");
      } else {
        setIsError(true);
        setErrorMessage(data.message);
      }
    } catch (error) {
      setIsError(true);
      setErrorMessage("An unexpected error occured");
    }
  }

  async function updateIngredient(id, ingredient) {
    const url = `${API_URL}/ingredients/${id}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ingredient),
      });

      if (response.status === 403) {
        setIsError(true);
        return setErrorMessage("You are not permitted to update ingredients");
      }

      const data = await response.json();

      if (data.status === "success") {
        setIsSuccess(true);
        setSuccessMessage("Ingredient updated successfully");
        setIngredient({});
        setFormFields([]);
        navigate("/dashboard");
      } else {
        setIsError(true);
        return setErrorMessage(data.message);
      }
    } catch (error) {
      setIsError(true);
      return setErrorMessage("An unexpected error occured");
    }
  }

  useEffect(() => {
    if (!token) {
      return navigate("/signin");
    }
  }, []);

  useEffect(() => {
    // Fetch the existing ingredient data if editing
    if (id) {
      setIngredient(ingredientData || {});
      setOriginalIngredient(ingredientData || {});
    }

    const fields = getFormFields(ingredientData);
    setFormFields(fields);
  }, [id, ingredientData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update the ingredient state
    setIngredient({
      ...ingredient,
      [name]: value,
    });

    // Update the formFields state with the new value
    setFormFields((prevFormFields) => {
      const updatedFields = [...prevFormFields];
      const fieldIndex = updatedFields.findIndex(
        (field) => field.name === name
      );

      if (fieldIndex !== -1) {
        updatedFields[fieldIndex] = {
          ...updatedFields[fieldIndex],
          value,
        };
      }

      return updatedFields;
    });
  };

  const handleSubmit = async () => {
    // Compare the current ingredient with the original
    const changedFields = {};
    for (const key in ingredient) {
      if (ingredient[key] !== originalIngredient[key]) {
        changedFields[key] = ingredient[key];
      }
    }

    console.log({ ingredient });
    if (id) {
      if (Object?.keys(changedFields).length === 0) {
        setIsError(true);
        return setErrorMessage("Please make changes in order to update");
      }
      await updateIngredient(id, changedFields);
    } else {
      if (Object?.keys(ingredient).length === 0) {
        setIsError(true);
        return setErrorMessage("Please fill in the fields");
      }
      await createIngredient(ingredient);
    }
  };

  return (
    <Layout>
      <Grid container spacing={2} columns={16} style={{ marginBottom: "30px" }}>
        <Grid item xs={16}>
          <Button
            variant="text"
            color="success"
            onClick={() => {
              navigate(-1);
            }}
          >
            Go Back
          </Button>
        </Grid>

        <Grid item xs={16}>
          <HeadingTitle text={`${id ? "Edit" : "Create"} Ingredient`} />
        </Grid>
      </Grid>

      <Subsection text={"Basic Information"} />
      <Grid container spacing={2} style={{ marginBottom: "30px" }}>
        {formFields &&
          formFields.slice(0, 10).map((field, index) => (
            <Grid item xs={field.grid} key={index} columnSpacing={1}>
              <TextField
                fullWidth
                margin="normal"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                size="small"
                value={field?.value || ""}
                onChange={handleChange}
              />
            </Grid>
          ))}
      </Grid>

      <Subsection text={"Physio-Chemical Properties"} />
      <Grid container spacing={2} style={{ marginBottom: "30px" }}>
        {formFields &&
          formFields.slice(11, 26).map((field, index) => (
            <Grid item xs={field.grid} key={index} columnSpacing={1}>
              <TextField
                fullWidth
                margin="normal"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                size="small"
                value={field?.value || ""}
                onChange={handleChange}
              />
            </Grid>
          ))}
      </Grid>

      <Subsection text={"Quantities of Ingredients"} />
      <Grid container spacing={2} style={{ marginBottom: "30px" }}>
        {formFields &&
          formFields.slice(26, 29).map((field, index) => (
            <Grid item xs={field.grid} key={index} columnSpacing={1}>
              <TextField
                fullWidth
                margin="normal"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                size="small"
                value={field?.value || ""}
                onChange={handleChange}
              />
            </Grid>
          ))}
      </Grid>

      <Subsection text={"Effectiveness"} />
      <Grid container spacing={2} style={{ marginBottom: "30px" }}>
        {formFields &&
          formFields.slice(29, 47).map((field, index) => (
            <Grid item xs={field.grid} key={index} columnSpacing={1}>
              <TextField
                fullWidth
                margin="normal"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                size="small"
                value={field?.value || ""}
                onChange={handleChange}
              />
            </Grid>
          ))}
      </Grid>

      <Subsection text={"Other Information"} />
      <Grid container spacing={2} style={{ marginBottom: "30px" }}>
        {formFields &&
          formFields.slice(47, 60).map((field, index) => (
            <Grid item xs={field.grid} key={index} columnSpacing={1}>
              <TextField
                fullWidth
                margin="normal"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                size="small"
                value={field?.value || ""}
                onChange={handleChange}
              />
            </Grid>
          ))}
      </Grid>

      <Subsection text={"Reference"} />
      <Grid container spacing={2} style={{ marginBottom: "30px" }}>
        {formFields &&
          formFields.slice(60, 70).map((field, index) => (
            <Grid item xs={field.grid} key={index} columnSpacing={1}>
              <TextField
                fullWidth
                multiline={field?.multiline || false}
                maxRows={field?.maxRows}
                margin="normal"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                size="small"
                value={field?.value || ""}
                onChange={handleChange}
              />
            </Grid>
          ))}
      </Grid>

      <Grid
        container
        spacing={2}
        style={{ marginBottom: "30px", marginTop: "30px" }}
      >
        <Grid item>
          <Paper
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "16px",
              display: "flex",
              justifyContent: "flex-end",
              zIndex: "10",
              marginTop: "30px",
            }}
            elevation={3}
          >
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="success"
                onClick={() => {
                  navigate("/dashboard");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
              >
                {id ? "Update" : "Create"} Ingredient
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <AlertBar
        open={isSuccess}
        message={successMessage}
        severity={"success"}
        onClose={handleIsSuccessState}
      />

      <AlertBar
        open={isError}
        message={errorMessage}
        severity={"error"}
        onClose={handleIsErrorState}
      />
    </Layout>
  );
};

export default IngredientForm;
